// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Clatter — every colour that carries meaning, in one place so tests/contrast.test.ts can hold it to
// WCAG 1.4.11 (≥3:1 for a non-text graphic) against every surface it can actually sit on.
//
// The governing decision: the SHEET IS LIGHT and everything else is dark. A roll-and-write is read
// the way paper is read, and a light sheet gives the four track colours their whole contrast budget
// — each one is a dark ink on parchment, so all four clear 3:1 against the sheet (measured
// 4.69–7.59:1) and, because a filled cell inverts, the number written on it clears the same bar.
//
// The four tracks are NOT 3:1 from EACH OTHER, and the tests say so rather than pretending
// otherwise: four dark inks on one light sheet necessarily land in the same luminance band
// (amber/teal measures 1.23:1, teal/violet 1.32:1). Hue is therefore a decorative channel here, and
// the one that actually carries is the GLYPH — every track prints its own (▲ ● ◆ ■) in its header,
// and the tracks are separated bands of the sheet rather than adjacent cells you must tell apart.
// So the sheet reads in greyscale and under any colour vision, but not because the inks differ in
// lightness. The dice are white with black pips, which is the one thing on screen
// nobody should have to think about.

export const PALETTE = {
  /** Page + chrome. */
  ground: '#12131A',
  panel: '#1C2030',
  panelHi: '#28304A',
  edge: '#3A4260',
  text: '#F4F6FB',
  textMuted: '#A6B0C6',
  gold: '#F0B429',

  /** The sheet — the one light surface, and the only thing marks are ever drawn on. */
  sheet: '#EDE4D0',
  /** A cell that is still empty: a slightly recessed tint of the sheet. */
  cellEmpty: '#E1D6BE',
  /** A cell the current die could legally go into. */
  cellLive: '#FBF3E0',

  /** The four tracks. Dark inks on parchment — see the note above. */
  amber: '#8A5A00',
  teal: '#0B6068',
  rust: '#9C2B18',
  violet: '#4C3391',

  /** Ink for a written number, and the rule text on the sheet. */
  ink: '#1A1B24',
  inkMuted: '#5C5648',

  /** A scored bonus pip / the chain flash. Drawn on the sheet. */
  bonus: '#7A3E00',

  /** Dice. White face, black pips — drawn on the dark tray. */
  die: '#F7F4EC',
  diePip: '#14151C',
  /** The die the active player took: it wears the gold ring. */
  dieTaken: '#F0B429',
} as const;

/** Every surface a meaningful mark can actually be drawn on. */
export const SURFACES = ['sheet', 'cellEmpty', 'cellLive'] as const;

/** Everything drawn ON the sheet. */
export const ON_SHEET = ['amber', 'teal', 'rust', 'violet', 'ink', 'bonus'] as const;

/** Everything drawn on the dark chrome (the dice tray, the HUD). */
export const ON_PANEL = ['die', 'gold', 'text'] as const;

/** Seat colours for the scoreboard. Distinct from the track inks, drawn on the dark panel. */
export const SEAT_COLOUR = ['#F0B429', '#5FD3C4', '#FF8A6B', '#B79CFF'];
export const SEAT_NAME = ['Gold', 'Jade', 'Coral', 'Lilac'];
/** Silhouette, not just hue — the channel that survives any colour vision. */
export const SEAT_SHAPE = ['disc', 'square', 'diamond', 'triangle'];
